<script>
	import { getElementsArray, getElement } from '@Utils/Elements.js'
	import { getIconHtml, getIconUrl } from '@Utils/Icons.js'
	import { getContext, onMount } from 'svelte'
	import { deconstructHoleId } from '@Utils/DataController.js'
	import RenderModal from '@Views/RenderModal.svelte'
	import Popover from '@Components/Popover.svelte'
	import WikiModal from '@Views/WikiModal.svelte'
	import LoadingIndicator from '@Components/LoadingIndicator.svelte'
	import FilterPanel from '@Views/FilterPanel.svelte'
	import ImportPanel from '@Views/ImportPanel.svelte'
	import LoopsPanel from '@Views/LoopsPanel.svelte'
	import GreenEditorModal from '@Views/GreenEditorModal.svelte'
	import { unitsFactors } from '@turf/helpers'
	import { nanoid } from 'nanoid'
	import { TreeDropDownMenu, BushDropDownMenu } from './Toolbar/TreeDropDownMenu.js'
	import { IconDropDownMenu, HoleIconDropDownMenu } from './Toolbar/IconDropDownMenu.js'

	const { getEditorCanvas, getController, getGuide, isAdmin } = getContext('editor')
	const editorCanvas = getEditorCanvas()
	const guide = getGuide()
	const controller = getController()
	const subscription = controller.getSubscription()
	const notPremumTooltip = 'Coming soon to premium subscribers'
	let hasActiveFilter = $state(false)
	let dropdownMenuElement
	let toolbarElement
	let currentTab = $state('commonElements')
	let greenEditorVisible = $state(false)
	const dropdownMenu = {
		icons: IconDropDownMenu,
		holeicons: HoleIconDropDownMenu,
		trees: TreeDropDownMenu,
		bushes: BushDropDownMenu
	}

	let dropdownVisible = $state(false)
	let modalvisible = $state(false)
	let filtersVisible = $state(false)
	let importVisible = $state(false)
	let loopsVisible = $state(false)
	let currentHoleId = $state(null)
	let currentCourseId = $state(null)
	let codes = controller.getCodes()
	let status = $state(controller.status ?? {})
	let selectedFrameData = null
	let mapService = editorCanvas.currentMapService
	let wikiPopover = $state(null)
	let courseMenuGroup = $state(null)
	let tabbarButtons = $state([])
	let tabCommon = $state([])
	let tabOther = $state([])
	let tools = $state([])
	let renderAndSave = $state(false)
	let holeMenuGroups = $state([])
	let isDarkMode = $state(window.isDarkMode)
	let currentTool = $state(editorCanvas.currentTool)
	$effect(() => {
		if (renderAndSave) {
			stopEditing()
		}
		for (let group of tabbarButtons) {
			for (let item of group.items) {
				if (item.id === 'filter') {
					if (!item.highlighted && hasActiveFilter) {
						updateItem('filter', { highlighted: true, tooltip: 'You have active element filters' })
					} else if (item.highlighted && !hasActiveFilter) {
						updateItem('filter', { highlighted: false, tooltip: 'Filter elements' })
					}
				}
			}
		}
	})
	function stopEditing() {
		editorCanvas.unfocus()
		editorCanvas.fitAllFrames()
	}
	function setupMenu() {
		let elements = getElementsArray().filter((element) => element.group !== 'Game' && element.name !== 'iconcollection')
		let _common = {}
		let _other = {}
		for (let e of elements) {
			if (e.enabled === false) {
				continue
			}
			if (e.frameMenuOnly === true) {
				continue
			}
			if (!e.subscriptions.includes(subscription)) {
				continue
			}
			let tabGroup = _common
			if (e.tabGroup === 'other') {
				tabGroup = _other
			}
			if (tabGroup[e.group] === undefined) {
				tabGroup[e.group] = {
					name: e.group,
					items: []
				}
			}
			if (e.name === 'icon' || e.name === 'holeicon') {
				e.icon = localStorage.getItem(`tool_${e.name}_iconName`) ?? e.defaultValues.iconName
			}
			e.subscriptions = e.subscriptions ?? ['basic']
			e.type = 'drawtool'
			e.id = e.name
			e.disabled = true
			e.selected = false
			tabGroup[e.group].items.push(e)
		}
		tabCommon = Object.values(_common)
		tabOther = Object.values(_other)
		let miscItems = [
			{
				type: 'measure',
				id: 'measure',
				icon: 'Ruler',
				title: 'Measure',
				tooltip: 'Measure distance between two or more points'
			},
			{
				type: 'union',
				id: 'union',
				icon: 'Union',
				title: 'Combine',
				tooltip: 'Combine two elements of the same kind into a single element. The elements must be intersecting.'
			}
		]
		tools.push({
			name: 'Misc',
			items: miscItems
		})
		if (isAdmin) {
			tools.push({
				name: 'Admin',
				items: [
					{
						type: 'importData',
						id: 'importData',
						icon: 'Import',
						title: 'Import data from file',
						tooltip: 'Import data from file'
					},
					{
						type: 'simplifyAllPolygons',
						id: 'simplifyAllPolygons',
						icon: 'Simplify',
						title: 'Simplify all polygons',
						tooltip: 'Simplify all polygons in the map. This might cause loss of detail in the polygons. This action cannot be undone'
					},
					{
						type: 'resetElementsToDefaultOptions',
						id: 'resetElementsToDefaultOptions',
						icon: 'Reset',
						title: 'Reset',
						tooltip: 'Reset all element to default options. Some options are protected such as tree type, rotation and size of trees. This action cannot be undone.'
					},
					{
						type: 'removeAllOverlappingElements',
						id: 'removeAllOverlappingElements',
						icon: 'RemoveOverlapping',
						title: 'Remove overlapping elements',
						tooltip: 'Remove all overlapping elements'
					}
				]
			})
		}

		window.addEventListener('colorSchemeChanged', (e) => {
			isDarkMode = e.isDarkMode
		})
		var other = {
			name: 'Other',
			items: [
				{
					id: 'filter',
					type: 'filter',
					icon: 'Filter',
					title: 'Filter',
					tooltip: 'Filter elements'
				}
			]
		}

		if (['premium'].includes(subscription)) {
			other.items.push({
				type: 'loops',
				icon: 'Loops',
				title: 'Loops',
				tooltip: 'Manage overview'
			})
		}
		tabbarButtons.push(other)
		tabbarButtons.push({
			name: 'Maps',
			items: [
				{
					image: getIconUrl('GoogleMapsLogo'),
					title: 'Goolge Maps',
					type: 'mapservice',
					id: 'google',
					tooltip: 'Google Maps',
					disabled: mapService === 'google',
					selected: mapService === 'google'
				},
				{
					image: getIconUrl('AzureMapsLogo'),
					title: 'Azure Maps',
					type: 'mapservice',
					id: 'azuremaps',
					tooltip: 'Azure Maps',
					disabled: mapService === 'azuremaps',
					selected: mapService === 'azuremaps'
				}
			]
		})
		tabbarButtons.push({
			name: 'Other',
			items: [
				{
					type: 'help',
					icon: 'Help',
					title: 'Help',
					tooltip: 'Help'
				},
				{
					type: 'save',
					icon: 'Save',
					title: 'Done',
					tooltip: 'Done'
				}
			]
		})
	}
	function enableAllOfType(type) {
		for (let group of tabCommon) {
			for (let item of group.items) {
				if (item.subscriptions && !item.subscriptions.includes(subscription)) {
					continue
				}
				if (item.type === type && item.frameOnly === false) {
					item.disabled = false
				}
			}
		}
		for (let group of tabOther) {
			for (let item of group.items) {
				if (item.subscriptions && !item.subscriptions.includes(subscription)) {
					continue
				}
				if (item.type === type && item.frameOnly === false) {
					item.disabled = false
				}
			}
		}
		tabOther = [...tabOther]
		tabCommon = [...tabCommon]
	}
	function updateItem(id, props) {
		if (!id) {
			return
		}
		for (let group of tabCommon) {
			for (let item of group.items) {
				if (item.id === id) {
					Object.assign(item, props)
				}
			}
		}
		for (let group of tabOther) {
			for (let item of group.items) {
				if (item.id === id) {
					Object.assign(item, props)
				}
			}
		}
		if (tools) {
			for (let group of tools) {
				for (let item of group.items) {
					if (item.id === id) {
						Object.assign(item, props)
					}
				}
			}
			tools = [...tools]
		}
		if (tabbarButtons) {
			for (let group of tabbarButtons) {
				for (let item of group.items) {
					if (item.id === id) {
						Object.assign(item, props)
					}
				}
			}
			tabbarButtons = [...tabbarButtons]
		}
		if (courseMenuGroup) {
			for (let group of courseMenuGroup) {
				for (let item of group.items) {
					if (item.id === id) {
						Object.assign(item, props)
					}
				}
			}
			courseMenuGroup = [...courseMenuGroup]
		}
		if (holeMenuGroups) {
			for (let group of holeMenuGroups) {
				for (let item of group.items) {
					if (item.id === id) {
						Object.assign(item, props)
					}
				}
			}
			holeMenuGroups = [...holeMenuGroups]
		}
		tabCommon = [...tabCommon]
		tabOther = [...tabOther]
	}
	setupMenu()
	editorCanvas.on('mapServiceChanged', function (service) {
		updateItem('google', { disabled: service === 'google', selected: service === 'google' })
		updateItem('bing', { disabled: service === 'bing', selected: service === 'bing' })
		updateItem('azuremaps', { disabled: service === 'azuremaps', selected: service === 'azuremaps' })
		guide.done('chooseMap')
	})
	controller.on('fetchCompleted', function () {
		enableAllOfType('drawtool')
	})
	controller.on('alertsUpdated', function (holes) {
		if (currentHoleId) {
			updateHoleMenu(currentHoleId)
		}
		if (currentCourseId) {
			updateCourseMenu(currentCourseId)
		}
	})
	controller.on('courseAlertsUpdates', function (data) {
		if (currentCourseId) {
			updateCourseMenu(currentCourseId)
		}
	})
	editorCanvas.on('currentToolChange', function (t, element) {
		updateItem(currentTool, { selected: false })
		updateItem(t, { selected: true })
		currentTool = t
		let userTip = null
		if (t) {
			if (element) {
				if (element.tooltype === 'Polygon' || element.tooltype === 'LineString') {
					userTip = {
						message: `User tip for ${element.title}`,
						details: ['Hold down shift and draw on freehand using the left mouse button. Release mouse button to finish the element.', 'Press ESC to deselect the tool.']
					}
				} else {
					userTip = {
						message: `User tip for ${element.title}`,
						details: ['Press ESC to deselect the tool.']
					}
				}
			} else if (t == 'measure') {
				userTip = {
					message: `User tip when measuring distance`,
					details: ['Click on the map to measure distance, you can even click more than once.', 'Press ESC to deselect the tool.']
				}
			} else if (t == 'union') {
				userTip = {
					message: `User tip when combining elements`,
					details: ['You can only combine elements that are intersecting and of the same kind.', 'Press ESC to cancel the tool.']
				}
			}
			if (userTip) {
				window.dispatchEvent(
					new CustomEvent('user-tip', {
						detail: {
							visibleFor: 10000,
							id: 'user-tip',
							message: userTip.message,
							details: userTip.details,
							action: {
								label: 'Stop showing tips',
								handler: () => {
									localStorage.setItem('hideUserTips', true)
								}
							}
						}
					})
				)
			}
		}
	})
	editorCanvas.on('elementSelected', function (n, o) {
		if (n && n.feature.get('holeId')) {
			let el = n.getElement()
			if (el.onePerFrame) {
				updateItem(n.getElementName(), { selected: true })
			}
		}
		if (o && o.feature.get('holeId')) {
			updateItem(o.getElementName(), { selected: false })
		}
	})
	controller.on('statusUpdated', function (s) {
		status = s
	})
	function getColor(code) {
		if (isDarkMode) {
			if (code === codes.error) {
				return '#F9423A'
			}
			if (code === codes.idle) {
				return '#26D07C'
			}
			if (code === codes.hasUnsavedData || code === codes.hasUnpublishedData) {
				return '#E48B23'
			}
			if (code === codes.savingToStoragea) {
				return '#B1B5CE'
			}
			if (code === codes.loading) {
				return '#5200E9'
			}
			return '#5200E9'
		}
		if (code === codes.error) {
			return '#F9423A'
		}
		if (code === codes.idle) {
			return '#26D07C'
		}
		if (code === codes.hasUnsavedData || code === codes.hasUnpublishedData) {
			return '#E48B23'
		}
		if (code === codes.savingToStorage) {
			return '#B1B5CE'
		}
		if (code === codes.loading) {
			return '#280071'
		}
		return '#280071'
	}
	function click(item) {
		if (item.feature) {
			editorCanvas.selectFeature(item.feature, true)
		} else if (item.type === 'drawtool') {
			if (item.name === 'icon') {
				editorCanvas.activateTool(item.name, {
					iconName: localStorage.getItem('tool_icon_iconName') ?? dropdownMenu.icons[0].props.iconName
				})
			} else if (item.name === 'holeicon') {
				editorCanvas.activateTool(item.name, {
					iconName: localStorage.getItem('tool_holeicon_iconName') ?? dropdownMenu.holeicons[0].props.iconName
				})
			} else {
				editorCanvas.activateTool(item.name, item.props)
			}
		} else if (item.type === 'measure') {
			editorCanvas.activateTool('measure')
		} else if (item.type === 'mapservice') {
			editorCanvas.setMapService(item.id)
		} else if (item.type === 'help') {
			modalvisible = true
		} else if (item.type === 'save') {
			renderAndSave = true
		} else if (item.type === 'filter') {
			filtersVisible = !filtersVisible
		} else if (item.type === 'loops') {
			loopsVisible = !loopsVisible
		} else if (item.type === 'union') {
			editorCanvas.activateTool('union')
		} else if (item.type === 'simplifyAllPolygons') {
			if (confirm('Are you sure you want to simplify all polygons? This action cannot be undone.')) {
				editorCanvas.simplifyAllPolygons()
				controller.save(true)
			}
		} else if (item.name === 'greenEditor') {
			greenEditorVisible = true
		} else if (item.name === 'playline') {
			editorCanvas.activateTool('playline')
		} else if (item.type === 'resetElementsToDefaultOptions') {
			if (confirm('Are you sure you want to reset all elements to default options? This action cannot be undone.')) {
				editorCanvas.resetElementsToDefaultOptions()
				controller.save(true)
			}
		} else if (item.type === 'removeAllOverlappingElements') {
			if (confirm('Are you sure you want to remove all overlapping elements? This action cannot be undone.')) {
				editorCanvas.removeAllOverlappingElements()
				controller.save(true)
			}
		} else if (item.type === 'importData') {
			importVisible = !importVisible
		}
	}
	function getTooltip(group, item) {
		let suffix = ''
		if (item.subscriptions && !item.subscriptions.includes(subscription)) {
			suffix = ' - ' + notPremumTooltip
		}
		if (item.tooltip) {
			return [item.tooltip, suffix].join('')
		}
		return [group.name, ' - ', item.title, suffix].join('')
	}
	function updateCourseMenu(courseId) {
		if (!courseId) {
			for (let group of tabCommon) {
				for (let item of group.items) {
					if (item.name === 'focus') {
						item.disabled = true
					}
					if (item.name === 'green') {
						item.disabled = true
					}
					if (item.name === 'tee') {
						item.disabled = true
					}
				}
			}
			courseMenuGroup = null
			tabCommon = [...tabCommon]
			tabOther = [...tabOther]
			return
		}
		let arr = []
		arr.push({
			name: 'Other',
			items: [
				{
					id: 'outline',
					name: 'outline',
					type: 'selection',
					icon: 'EditFrame',
					title: 'Hole outline',
					feature: selectedFrameData?.feature,
					tooltip: 'Select to edit hole outline',
					props: {
						courseId: courseId
					},
					selected: false
				},
				{
					id: 'focus',
					name: 'focus',
					type: 'drawtool',
					icon: 'Focus',
					title: 'Focus',
					props: {
						courseId: courseId
					},
					feature: selectedFrameData?.source.getFeatures().find((f) => f.get('elementName') === 'focus'),
					selected: false
				},
				{
					id: 'text',
					name: 'text',
					type: 'drawtool',
					icon: 'Text',
					title: 'Text label',
					tooltip: 'Add text label',
					props: {
						courseId: courseId
					},
					selected: false
				},
				{
					id: 'iconcollection',
					name: 'iconcollection',
					icon: 'IconCollection',
					title: 'Icon collection',
					tooltip: 'Collection of icons',
					type: 'drawtool',
					disabled: !['premium'].includes(subscription),
					visible: ['premium'].includes(subscription),
					optional: true,
					selected: false
				}
			]
		})
		courseMenuGroup = arr

		tabCommon = [...tabCommon]
		tabOther = [...tabOther]
	}
	function updateHoleMenu(holeId) {
		courseMenuGroup = null
		if (!holeId) {
			for (let group of tabCommon) {
				for (let item of group.items) {
					if (item.name === 'focus') {
						item.disabled = true
					}
					if (item.name === 'green') {
						item.disabled = true
					}
					if (item.name === 'tee') {
						item.disabled = true
					}
				}
			}
			tabCommon = [...tabCommon]
			tabOther = [...tabOther]
			return
		}
		let props = deconstructHoleId(holeId)
		let hole = controller.data.holes.find((h) => h.id == props.holeId)
		if (!hole) {
			return
		}
		let arr = []
		arr.push({
			name: 'Other',
			items: [
				{
					id: 'outline',
					name: 'outline',
					type: 'selection',
					icon: 'EditFrame',
					title: 'Hole outline',
					feature: selectedFrameData?.feature,
					tooltip: 'Select to edit hole outline',
					props: {
						holeId: holeId
					},
					selected: false
				},
				{
					id: 'focus',
					name: 'focus',
					type: 'drawtool',
					icon: 'Focus',
					title: 'Focus',
					tooltip: hole.mapInfo.hasFocus === true ? 'Select to edit focus' : 'Add Focus',
					props: {
						holeId: holeId
					},
					feature: selectedFrameData?.source.getFeatures().find((f) => f.get('elementName') === 'focus'),
					//disabled: hole.mapInfo.hasFocus === true,
					selected: false
				},
				{
					id: 'playline',
					name: 'playline',
					type: 'drawtool',
					icon: 'Playline',
					title: 'Edit play line',
					tooltip: 'Edit the play line',
					disabled: !['premium'].includes(subscription),
					visible: ['premium'].includes(subscription),
					props: {
						holeId: holeId
					},
					optional: true,
					selected: currentTool === 'playline'
				}
			]
		})
		arr.push({
			name: 'Other',
			items: [
				{
					id: 'iconcollection',
					name: 'iconcollection',
					icon: 'IconCollection',
					title: 'Icon collection',
					tooltip: 'Collection of icons',
					type: 'drawtool',
					optional: true,
					disabled: !['premium'].includes(subscription),
					visible: ['premium'].includes(subscription),
					selected: false
				},
				{
					id: 'holeicon',
					name: 'holeicon',
					icon: 'NextTee',
					dropdown: 'holeicons',
					title: 'Hole icons',
					tooltip: 'Add hole specific icons',
					type: 'drawtool',
					disabled: !['premium'].includes(subscription),
					visible: ['premium'].includes(subscription),
					props: {
						iconName: 'NextTee',
						holeId: holeId
					},
					selected: false
				},
				{
					id: 'text',
					name: 'text',
					type: 'drawtool',
					icon: 'Text',
					title: 'Text label',
					visible: ['premium'].includes(subscription),
					tooltip: 'Add text label',
					props: {
						holeId: holeId,
						scale: 0.3
					},
					optional: true,
					selected: false
				}
			]
		})

		arr.push({
			name: 'Green image',
			items: [
				{
					id: 'greenfocus',
					name: 'greenfocus',
					type: 'drawtool',
					icon: 'GreenFocus',
					title: 'Green focus',
					tooltip: 'Add green focus',
					disabled: !['premium'].includes(subscription),
					visible: ['premium'].includes(subscription),
					props: {
						holeId: holeId
					},
					optional: true,
					feature: selectedFrameData?.source.getFeatures().find((f) => f.get('elementName') === 'greenfocus'),
					selected: false
				},
				{
					id: 'greenEditor',
					name: 'greenEditor',
					type: 'modal',
					icon: 'EditGreen',
					title: 'Edit green',
					tooltip: 'Edit green image by adding zones and inclination',
					disabled: !['premium'].includes(subscription) || hole.mapInfo.hasGreenFocus === false,
					visible: ['premium'].includes(subscription),
					optional: true,
					props: {
						holeId: holeId
					},
					selected: false
				}
			]
		})
		arr.push({
			name: 'Green points',
			items: [
				{
					id: 'greenfront',
					name: 'greenfront',
					icon: 'GreenFront',
					title: 'Front green',
					tooltip: hole.mapInfo.green.front ? 'Select to edit Front green' : 'Add Front green',
					type: 'drawtool',
					props: {
						holeId: holeId
					},
					feature: selectedFrameData?.source.getFeatures().find((f) => f.get('elementName') === 'greenfront'),
					//disabled: hole.mapInfo.green.front === true,
					selected: false
				},
				{
					id: 'greencenter',
					name: 'greencenter',
					icon: 'GreenCenter',
					title: 'Center green',
					tooltip: hole.mapInfo.green.center ? 'Select to edit - Center green' : 'Add - Center green',
					type: 'drawtool',
					props: {
						holeId: holeId
					},
					feature: selectedFrameData?.source.getFeatures().find((f) => f.get('elementName') === 'greencenter'),
					//disabled: hole.mapInfo.green.center === true,
					selected: false
				},
				{
					id: 'greenback',
					name: 'greenback',
					icon: 'GreenBack',
					title: 'Back green',
					tooltip: hole.mapInfo.green.back ? 'Select to edit - Back green' : 'Add - Back green',
					type: 'drawtool',
					props: {
						holeId: holeId
					},
					feature: selectedFrameData?.source.getFeatures().find((f) => f.get('elementName') === 'greenback'),
					//disabled: hole.mapInfo.green.back === true,
					selected: false
				}
			]
		})
		let tees = controller.data.holeTees[props.holeId]
		arr.push({
			name: 'Tees',
			items: tees.map((tee) => {
				let el = getElement('tee' + tee.elementId)
				return {
					id: el.name,
					name: el.name,
					type: 'drawtool',
					icon: el.icon,
					title: tee.teeAlias,
					tooltip: hole.mapInfo.tee[tee.elementId] !== true ? `Add - ${tee.teeAlias} tee` : `Select to edit - ${tee.teeAlias} tee`,
					props: {
						holeId: holeId
					},
					feature: selectedFrameData?.source.getFeatures().find((f) => f.get('elementName') === 'tee' + tee.elementId),
					//disabled: hole.mapInfo.tee[tee.elementId] === true,
					selected: false
				}
			})
		})
		holeMenuGroups = arr
		courseMenuGroup = []
		for (let group of tabCommon) {
			for (let item of group.items) {
				if (item.name === 'focus') {
					item.feature = selectedFrameData?.source.getFeatures().find((f) => f.get('elementName') === 'focus')
					item.disabled = false
				}
				if (item.name === 'green') {
					item.feature = selectedFrameData?.source.getFeatures().find((f) => f.get('elementName') === 'green')
					item.disabled = false
				}
				if (item.name === 'tee') {
					item.disabled = false
				}
			}
		}
		tabCommon = [...tabCommon]
		tabOther = [...tabOther]
	}
	const onFrameFocused = (data) => {
		selectedFrameData = data
		if (data) {
			currentHoleId = null
			holeMenuGroups = null
			updateHoleMenu(null)
			updateCourseMenu(null)
			let courseId = data.feature.get('courseId')
			let holeId = data.feature.get('holeId')
			if (holeId) {
				currentHoleId = data.feature.get('holeId')
				updateHoleMenu(currentHoleId)
			} else if (courseId) {
				currentCourseId = courseId
				updateCourseMenu(courseId)
			}
		} else {
			currentCourseId = null
			currentHoleId = null
			holeMenuGroups = null
			updateHoleMenu(null)
			updateCourseMenu(null)
		}
	}
	function showDropdown(e, item) {
		if (item.dropdown) {
			dropdownMenuElement.innerHTML = ''
			let dd = dropdownMenu[item.dropdown]
			dropdownMenuElement.classList.remove('grid-cols-4')
			dropdownMenuElement.classList.remove('grid-cols-1')
			if (item.dropdown.includes('icons')) {
				dropdownMenuElement.classList.add('grid-cols-4')
			} else {
				dropdownMenuElement.classList.add('grid-cols-1')
			}
			for (let i of dd) {
				let btn = document.createElement('button')
				btn.classList.add('dd-btn', 'p-1', 'rounded')
				if (i.icon) {
					btn.innerHTML = getIconHtml(i.icon, null, 25, 25)
				} else {
					if (isDarkMode) {
						btn.innerHTML = `<div class="text-xs text-white flex flex-1">${i.title}</div>`
					} else {
						btn.innerHTML = `<div class="text-xs flex flex-1">${i.title}</div>`
					}
				}
				btn.setAttribute('tooltip', i.tooltip)
				btn.addEventListener('click', () => {
					if (i.props.iconName) {
						localStorage.setItem(`tool_${i.tool}_iconName`, i.props.iconName)
					}
					if (i.icon) {
						updateItem(item.id, { icon: i.icon })
					}
					editorCanvas.activateTool(i.tool, i.props)
					hideDropdown()
				})
				dropdownMenuElement.appendChild(btn)
			}
			let ref = document.getElementById('dropdown-' + item.id)
			if (!ref) {
				return
			}
			let rect = ref.getBoundingClientRect()
			let ddRect = dropdownMenuElement.getBoundingClientRect()
			let bodyWidth = document.body.clientWidth
			dropdownVisible = true
			dropdownMenuElement.style.right = bodyWidth - rect.right + 'px'
			dropdownMenuElement.style.top = rect.y + rect.height + 'px'

			document.addEventListener('mousedown', onMouseDown)
		}
	}
	function hideDropdown() {
		document.removeEventListener('mousedown', onMouseDown)
		if (!dropdownMenuElement) {
			return
		}
		dropdownVisible = false
	}
	function toggleDropdown(e, item) {
		if (!dropdownVisible) {
			showDropdown(e, item)
		} else {
			hideDropdown()
		}
	}
	const onMouseDown = function (e) {
		if (!dropdownVisible) {
			return
		}
		let t = e.target
		while (t) {
			if (t.dataset.role === 'dropdownarrrow') {
				return
			}
			if (t.id === 'dropdownmenu') {
				return
			}
			t = t.parentElement
		}
		hideDropdown()
	}
	onMount(() => {
		if (controller.initalFetchCompleted) {
			enableAllOfType('drawtool')
		}
		toolbarElement
		let r = toolbarElement.getBoundingClientRect()
		editorCanvas.viewPadding[0] = r.height + r.top + 30 + 50
	})
	function changeTab(tab) {
		currentTab = tab
	}
	editorCanvas.on('frameFocused', onFrameFocused)
</script>

<div bind:this={toolbarElement} class="toolbar">
	<div class="box box-shadow content flex flex-col gap-0" data-subscription={subscription}>
		<div class="tabs" class:dark-tabs={isDarkMode}>
			<div class="status-item">
				<button disabled={status.loading || ![codes.hasUnsavedData, codes.hasUnpublishedData].includes(status.code)} onclick={() => controller.save(false, true)} class="save-btn">
					<LoadingIndicator isLoading={status.loading} color={getColor(status.code)} />
					<div class="status-message" tooltip={status.tooltip}>{status.message}</div>
				</button>
			</div>
			<button class="tab" class:tab-active={currentTab === 'commonElements'} disabled={currentTab === 'commonElements'} onclick={() => changeTab('commonElements')} c-tooltip="Map elements such as fairway, forest etc">Common</button>
			<button class="tab" class:tab-active={currentTab === 'otherElements'} disabled={currentTab === 'otherElements'} onclick={() => changeTab('otherElements')} c-tooltip="Map elements such as stone wall, gravel etc">Other</button>
			<button class="tab" class:tab-active={currentTab === 'tools'} disabled={currentTab === 'tools'} onclick={() => changeTab('tools')} c-tooltip="Tools such as union, measure and more">Tools</button>
			<div class="tab-spacer"></div>

			{#each tabbarButtons as group}
				<div class="tab-btn-group">
					{#each group.items as item}
						{#if item.image}
							<button class="tab-btn flex-1 max-h-8 w-7 flex items-center justify-center" class:highlighted={item.highlighted} selected={item.selected} disabled={item.disabled} onclick={() => click(item)}>
								<img style="height: 22px" src={item.image} alt={item.title} tooltip={getTooltip(group, item)} />
							</button>
						{:else}
							<button class="tab-btn p-2 max-h-7 flex-1 flex items-center justify-center" class:highlighted={item.highlighted} tooltip={getTooltip(group, item)} disabled={item.disabled} onclick={() => click(item)}>
								{@html getIconHtml(item.icon, 'dark:bg-white')}
							</button>
						{/if}
					{/each}
				</div>
			{/each}
		</div>
		{#if currentTab === 'commonElements'}
			<div class="flex flex-row gap-0 menu-row">
				{#each tabCommon as group}
					<div class="group flex flex-row items-center justify-center p-1" class:dark-group-border={isDarkMode}>
						{#each group.items.filter((i) => i.visible !== false) as item}
							{#if item.image}
								<button class="map-service-btn max-h-9 flex-1 flex items-center justify-center" tooltip={getTooltip(group, item)} selected={item.selected} disabled={item.disabled} onclick={() => click(item)}>
									<img style="height: 22px" src={item.image} alt={item.title} />
								</button>
							{:else if dropdownMenu[item.dropdown]}
								<div id="dropdown-{item.id}" class="dd-container max-h-9 flex-1 flex items-center justify-center" selected={item.selected} data-disabled={item.disabled}>
									<button class="dd-btn dd-btn-left p-2" disabled={item.disabled} onclick={() => click(item)} tooltip={getTooltip(group, item)} data-tooltip-offset="15">
										{@html getIconHtml(item.icon, isDarkMode ? '' : 'image-bordered')}
									</button>
									<button class="dd-btn dd-btn-arrow p-1 h-9 flex items-center justify-center" data-role="dropdownarrrow" disabled={item.disabled} onclick={(e) => toggleDropdown(e, item)}>{@html getIconHtml('DropdownChevronDown', 'dark:bg-white', 6, 4)}</button>
								</div>
							{:else}
								<button class="menu-btn p-2 max-h-9 flex-1 flex items-center justify-center" tooltip={getTooltip(group, item)} selected={item.selected} disabled={item.disabled} onclick={() => click(item)}>
									{@html getIconHtml(item.icon, 'dark:bg-white')}
								</button>
							{/if}
						{/each}
						<div class="flex-1"></div>
					</div>
				{/each}
			</div>
		{/if}
		{#if currentTab === 'otherElements'}
			<div class="flex flex-row gap-0 menu-row">
				{#each tabOther as group}
					<div class="group flex flex-row items-center justify-center p-1" class:dark-group-border={isDarkMode}>
						{#each group.items.filter((i) => i.visible !== false) as item}
							{#if item.image}
								<button class="map-service-btn max-h-9 flex-1 flex items-center justify-center" tooltip={getTooltip(group, item)} selected={item.selected} disabled={item.disabled} onclick={() => click(item)}>
									<img style="height: 22px" src={item.image} alt={item.title} />
								</button>
							{:else if dropdownMenu[item.dropdown]}
								<div id="dropdown-{item.id}" class="dd-container max-h-9 flex-1 flex items-center justify-center" selected={item.selected} data-disabled={item.disabled}>
									<button class="dd-btn dd-btn-left p-2" disabled={item.disabled} onclick={() => click(item)} tooltip={getTooltip(group, item)} data-tooltip-offset="15">
										{@html getIconHtml(item.icon, isDarkMode ? '' : 'image-bordered')}
									</button>
									<button class="dd-btn dd-btn-arrow p-1 h-9 flex items-center justify-center" data-role="dropdownarrrow" disabled={item.disabled} onclick={(e) => toggleDropdown(e, item)}>{@html getIconHtml('DropdownChevronDown', 'dark:bg-white', 6, 4)}</button>
								</div>
							{:else}
								<button class="menu-btn p-2 max-h-9 flex-1 flex items-center justify-center" tooltip={getTooltip(group, item)} selected={item.selected} disabled={item.disabled} onclick={() => click(item)}>
									{@html getIconHtml(item.icon, 'dark:bg-white')}
								</button>
							{/if}
						{/each}
					</div>
				{/each}
				{#if (!['premium'].includes(subscription))}
				<div class="flex-1"></div>
				{/if}
			</div>
		{/if}
		{#if currentTab === 'tools'}
			<div class="flex flex-row gap-0 menu-row">
				{#each tools as group}
					<div class="group flex flex-row items-center justify-center p-1" class:dark-group-border={isDarkMode}>
						{#each group.items.filter((i) => i.visible !== false) as item}
							{#if item.image}
								<button class="map-service-btn max-h-9 flex-1 flex items-center justify-center" tooltip={getTooltip(group, item)} selected={item.selected} disabled={item.disabled} onclick={() => click(item)}>
									<img style="height: 22px" src={item.image} alt={item.title} />
								</button>
							{:else if dropdownMenu[item.dropdown]}
								<div id="dropdown-{item.id}" class="dd-container max-h-9 flex-1 flex items-center justify-center" selected={item.selected} data-disabled={item.disabled}>
									<button class="dd-btn dd-btn-left p-2" disabled={item.disabled} onclick={() => click(item)} tooltip={getTooltip(group, item)} data-tooltip-offset="15">
										{@html getIconHtml(item.icon, isDarkMode ? '' : 'image-bordered')}
									</button>
									<button class="dd-btn dd-btn-arrow p-1 h-9 flex items-center justify-center" data-role="dropdownarrrow" disabled={item.disabled} onclick={(e) => toggleDropdown(e, item)}>{@html getIconHtml('DropdownChevronDown', 'dark:bg-white', 6, 4)}</button>
								</div>
							{:else}
								<button class="menu-btn p-2 max-h-9 flex-1 flex items-center justify-center" tooltip={getTooltip(group, item)} selected={item.selected} disabled={item.disabled} onclick={() => click(item)}>
									{@html getIconHtml(item.icon, 'dark:bg-white')}
								</button>
							{/if}
						{/each}
					</div>
				{/each}
			</div>
		{/if}
		{#if (holeMenuGroups && holeMenuGroups.length > 0) || (courseMenuGroup && courseMenuGroup.length > 0)}
			<div class="flex flex-row gap-0 menu-row dark:bg-neutral-200/5 bg-neutral-200">
				{#if holeMenuGroups}
					{#each holeMenuGroups as group}
						<div class="flex flex-row items-center justify-center p-1">
							{#each group.items.filter((i) => i.visible !== false) as item}
								{#if item.image}
									<button class="map-service-btn max-h-9 flex-1 flex items-center justify-center frame-info" class:frame-info-item-missing={!item.feature && !item.optional} class:frame-info-item-added={item.feature} tooltip={getTooltip(group, item)} selected={item.selected} disabled={item.disabled} onclick={() => click(item)}>
										<img style="height: 22px" src={item.image} alt={item.title} />
									</button>
								{:else if dropdownMenu[item.dropdown]}
									<div id="dropdown-{item.id}" class="dd-container max-h-9 flex-1 flex items-center justify-center" selected={item.selected} data-disabled={item.disabled}>
										<button class="dd-btn dd-btn-left p-2" disabled={item.disabled} onclick={() => click(item)} tooltip={getTooltip(group, item)} data-tooltip-offset="15">
											{@html getIconHtml(item.icon, isDarkMode ? '' : 'image-bordered')}
										</button>
										<button class="dd-btn dd-btn-arrow p-1 h-9 flex items-center justify-center" data-role="dropdownarrrow" disabled={item.disabled} onclick={(e) => toggleDropdown(e, item)}>{@html getIconHtml('DropdownChevronDown', 'dark:bg-white', 6, 4)}</button>
									</div>
								{:else}
									<button class="menu-btn p-2 max-h-9 flex-1 flex items-center justify-center frame-info" class:frame-info-item-missing={!item.feature && !item.optional} class:frame-info-item-added={item.feature} tooltip={getTooltip(group, item)} selected={item.selected} disabled={item.disabled} onclick={(e) => click(item)}>
										{@html getIconHtml(item.icon)}
									</button>
								{/if}
							{/each}
						</div>
					{/each}
				{/if}
				{#if courseMenuGroup}
					{#each courseMenuGroup as group}
						<div class="flex flex-row items-center justify-center p-1">
							{#each group.items.filter((i) => i.visible !== false) as item}
								{#if item.image}
									<button class="map-service-btn max-h-9 flex-1 flex items-center justify-center frame-info" class:frame-info-item-missing={!item.feature} class:frame-info-item-added={item.feature} tooltip={getTooltip(group, item)} selected={item.selected} disabled={item.disabled} onclick={() => click(item)}>
										<img style="height: 22px" src={item.image} alt={item.title} />
									</button>
								{:else}
									<button class="menu-btn p-2 max-h-9 flex-1 flex items-center justify-center frame-info" tooltip={getTooltip(group, item)} selected={item.selected} disabled={item.disabled} onclick={(e) => click(item)}>
										{@html getIconHtml(item.icon)}
									</button>
								{/if}
							{/each}
						</div>
					{/each}
				{/if}
			</div>
		{/if}
	</div>
</div>
<div bind:this={dropdownMenuElement} id="dropdownmenu" class="box-shadow content p-1 dropdown-menu grid grid-cols-4 gap-1" class:bg-neutral-200={!isDarkMode} class:box={isDarkMode} class:hidden={!dropdownVisible}></div>
<Popover behaviour="manual" bind:root={wikiPopover} id="helppopover" title="You can find the wiki here" anchor="helpbutton">
	<br />
	<button
		name="gotit"
		class="btn btn-sm btn-blue float-right"
		onclick={() => {
			guide.done('findWiki')
			if (wikiPopover.togglePopover) {
				wikiPopover.togglePopover(false)
			} else {
				wikiPopover.style.visibility = 'hidden'
			}
		}}>Got it!</button
	>
</Popover>
{#if greenEditorVisible}
	<GreenEditorModal bind:visible={greenEditorVisible} bind:holeId={currentHoleId} />
{/if}
<WikiModal bind:visible={modalvisible} on:close={() => {}} />
{#if renderAndSave}
	<RenderModal bind:visible={renderAndSave} />
{/if}
<FilterPanel bind:visible={filtersVisible} bind:hasActiveFilter></FilterPanel>
<ImportPanel bind:visible={importVisible}></ImportPanel>
<LoopsPanel bind:visible={loopsVisible}></LoopsPanel>

<style lang="postcss">
	.frame-info {
		position: relative;
	}
	.frame-info:after {
		position: absolute;
		bottom: -7px;
		content: '';
		width: 6px;
		height: 6px;
		border-radius: 3px 3px;
	}
	.frame-info-item-missing::after {
		background-color: rgba(194, 58, 58, 0.8);
	}
	.frame-info-item-added::after {
		/*background-color: rgba(58, 194, 58, 0.8);*/
	}
	.dropdown-menu {
		border-radius: 10px;
		position: absolute;
		top: 0;
		right: 0;
		z-index: 100;
	}
	.save-btn {
		display: flex;
		padding-left: 7px;
		padding-right: 7px;
		flex-direction: row;

		gap: 6px;
		align-items: center;
		border-radius: 8px;
	}
	.save-btn:disabled .status-message {
		opacity: 0.5;
	}
	.save-btn:hover:not([disabled]) {
		outline: theme('colors.blue.100') solid 2px;
	}
	.save-btn:focus-visible:not([disabled]) {
		background: rgba(226, 243, 255, 1);
		outline: theme('colors.blue.500') solid 2px;
	}
	.save-btn:active:not([disabled]) {
		background: theme('colors.blue.100');
		outline: theme('colors.blue.500') solid 2px;
	}
	.toolbar .content {
		border-radius: 8px 8px 8px 8px;
		pointer-events: all;
		/* overflow: hidden; */
		min-width: 680px;
	}
	.toolbar .content[data-subscription='premium'] {
		min-width: 680px;
	}
	.menu-row {
		border-bottom: 1px solid rgba(255, 255, 255, 0.2);
		justify-content: space-between;
	}
	.menu-row:last-child {
		border-bottom: none;
	}
	.toolbar {
		pointer-events: none;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;

		z-index: 50;
		position: absolute;
		top: 10px;
		left: 0;
		right: 0;
	}
	.group:first-child {
		margin-left: 10;
		border-left: none;
	}
	.status-message {
		user-select: none;
		font-size: 14px;
		width: 70px;
		padding-right: 4px;
		text-align: left;
	}
	.map-service-btn {
		opacity: 0.3;
		filter: saturate(0.5);
		margin-left: 5px;
		margin-right: 5px;
	}
	.map-service-btn * {
		pointer-events: none;
	}
	.map-service-btn:disabled {
		opacity: 0.5;
	}
	.map-service-btn:hover:not([disabled]) {
		opacity: 1;
		filter: saturate(1);
	}
	.map-service-btn:focus-visible:not([disabled]) {
		opacity: 1;
		filter: saturate(1);
	}
	.map-service-btn:active:not([disabled]) {
		opacity: 1;
		filter: saturate(1);
	}
	.map-service-btn[selected='true'] {
		opacity: 1;
		filter: saturate(1);
	}
</style>
